/** Append-only recovery records, outside normal slot/backup rotation.
 * Each record stores the exact pre-recovery pair atomically. Repeated boots
 * deduplicate identical pairs; a later failure never replaces older evidence.
 */
export interface RecoveryStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}
export function preserveFailedSave(store: RecoveryStorage, slot = 0): boolean {
  try {
    const key = `exotics-adventure.save.${String(slot)}`;
    const primary = store.getItem(key);
    const backup = store.getItem(`${key}.bak`);
    if (primary === null && backup === null) return false;
    const record = JSON.stringify({ primary, backup });
    for (let n = 0; n < 10000; n++) {
      const archiveKey = `${key}.recovery.${String(n)}`;
      const existing = store.getItem(archiveKey);
      if (existing === record) return true;
      if (existing !== null) continue;
      store.setItem(archiveKey, record);
      return store.getItem(archiveKey) === record;
    }
  } catch { /* denied/quota/readback failure: caller must not overwrite originals */ }
  return false;
}

/** Append-only live checkpoint before a player replaces the running ranch.
 * Does not rotate any slot. A failed write blocks replacement. */
export function preserveReplacementSave(store: RecoveryStorage, envelope: string): boolean {
  try {
    for (let n=0;n<10000;n++) {
      const key=`exotics-adventure.replacement.${String(n)}`;
      const prior=store.getItem(key);
      if(prior===envelope)return true;
      if(prior!==null)continue;
      store.setItem(key,envelope);
      return store.getItem(key)===envelope;
    }
  } catch { /* quota/denied: no safe replacement */ }
  return false;
}

export function latestReplacementSave(store: RecoveryStorage): string | null {
  let latest: string | null=null;
  for(let n=0;n<10000;n++) {
    const value=store.getItem(`exotics-adventure.replacement.${String(n)}`);
    if(value===null)break;
    latest=value;
  }
  return latest;
}
